import { Component, Input, Output, EventEmitter } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import {
  BaseAction,
  TriggerAction,
  ToggleAction,
  ActionType,
} from '../actions';

@Component({
  selector: 'app-button-base',
  template: '',
})
export class ButtonBaseComponent {
  @Input() action!: BaseAction;
  @Input() object: any;
  @Input() icon?: string;
  @Input() innerLabel?: string;
  @Input() disabled?: boolean | Observable<boolean>;
  @Input() active?: boolean = false;
  @Input() reversed: boolean = false;
  @Input() stopPropagation: boolean = false;
  @Input() displayIcon: boolean = true;
  @Input() displayTitle: boolean = true;
  @Input() type: string = 'button';

  @Output() clicked: EventEmitter<any> = new EventEmitter<any>();

  constructor() {}

  /**
   * Handles the click event
   */
  onClick(event: MouseEvent | TouchEvent): void {
    this._actionEvent(event);
  }

  private _actionEvent(event: MouseEvent | TouchEvent) {
    this.clicked.emit(event);

    if (this.stopPropagation) {
      event.stopPropagation();
    }

    if (!this.action) {
      return;
    }

    switch (this.action.type()) {
      case ActionType.TRIGGER: {
        const action = <TriggerAction>this.action;
        action.trigger(this.object);
        break;
      }
      case ActionType.TOGGLE: {
        const action = <ToggleAction>this.action;
        action.toggle();
        break;
      }
      default: {
        console.warn('Action is not recognized');
        break;
      }
    }
  }

  /**
   * @method getTitle
   * Retrieve title from action model or inner-label attribute
   * @returns the title string
   */
  getTitle(): string {
    return this.innerLabel || this.action?.title || '';
  }

  /**
   * @method getType
   * @returns the button type
   */
  getType(): string {
    return this.type;
  }

  /**
   * @method getIcon
   * @returns the registered action icon
   */
  getIcon(): string {
    return this.icon || this.action?.icon || '';
  }

  /**
   * @method isDisabled
   * @returns true if the action is not available, otherwise - false
   */
  isEnabled(): Observable<boolean> {
    return this.disabled != undefined
      ? typeof this.disabled == 'boolean'
        ? of(!this.disabled)
        : this.disabled.pipe(map((disabled) => !disabled))
      : this.action?.isEnable() || of(false);
  }

  /**
   * @method isActive
   * @returns true if the action is activated (in case toggle action), otherwise - false
   */
  isActive(): boolean {
    if (typeof this.active == 'boolean') {
      return this.active;
    }

    if (!this.action) {
      return false;
    }

    if (this.action.type() === ActionType.TOGGLE) {
      const action = <ToggleAction>this.action;
      return action.isToggle();
    }

    return false;
  }
}
